import { cn } from "@/libs/utils";
import { PDP_TEXT_LINK } from "@/components/tactsuit/pdp-text-link";
import { useTrans } from "@/utils/useTrans";
import { DisplayGameCardsCarousel } from "@/v2-components/pages/games/DisplayGameCardsCarousel";
import Image from "next/image";
import Link from "next/link";
import React from "react";

const BASE_PATH = "/images/tactsuit/common";

/**
 * ★**세 카드가 전부 같은 곳으로 갔다** (#5647 · 작성자 2026-09-09).
 *
 * 셋 다 `detailLink: "/software/player/?type=pcplayer"` 라, 어느 카드를 눌러도 **PC**
 * 소프트웨어 페이지로 갔다. 실측(dev 2026-09-09): PDP 6종
 * (`tactsuit-pro`·`tactsuit-air`·`tactsleeve`·`tactglove-dk3`·`tactvisor`·
 * `tactosy-for-feet`) 전부에서 이 카드가 뜨고, HTML 에 `?type=vrplayer`·
 * `?type=mobileplayer` 가 **한 건도 없었다**.
 *
 * ## ★스텁을 거치지 않고 **직결**한다
 *
 * `/software/player` 는 화면 없는 리다이렉트 스텁이다(`pages/software/player.tsx` —
 * `?type` 을 보고 `/games/{pc,vr}/software/` 로 보낸다). 그 파일 주석이 규약을 이미
 * 적어 뒀다 — *"최종 목적지로 **직결**한다(#5604 R1 M2) … 그 경로가 이제
 * 리다이렉트되므로 2홉이 된다"*. 종전 링크는 **스텁 1홉 + 리다이렉트 1홉**이라 같은
 * 문제였다.
 *
 * ## 목적지 앵커는 **이미 있다**
 *
 * `sw-player-section.tsx:72` 가 `id={section.anchor}` 를 달고 `scroll-mt-28` 로 sticky
 * 헤더 아래 착지를 잡는다. 실측(렌더된 HTML): `sw-pc-player` · `sw-vr-player` ·
 * `sw-mobile-player` 세 개가 다 나온다.
 *
 * ```
 *   /games/vr/software/   sw-pc-player · sw-vr-player · sw-mobile-player   (세 개 다)
 *   /games/pc/software/   sw-* 앵커 **없음**
 * ```
 *
 * ★**세 앵커가 전부 VR 페이지에 있다.** 그 페이지가 PC VR·Standalone·PS VR2 세 섹션을
 * 다 갖는다. 처음엔 PC 카드도 `#sw-pc-player` 로 보냈는데 **PC 페이지엔 그 id 가 없어**
 * 착지하지 않았다(실측: `섹션있음 false` · `scrollY 0`). VR 페이지에서 본 앵커를 PC
 * 페이지에도 있으리라 **가정한 것**이 원인이다 — 앵커는 **그 페이지에서** 확인한다.
 *
 * 그래서 PC 카드는 해시 없이 `/games/pc/software/` 로 둔다(종전 도착지와 같다 —
 * 스텁 홉만 걷었다).
 *
 * ⚠️ 앵커 문자열을 여기 **손으로 적었다** — 정의는 `sw-config.ts` 의 `PLAYER_SECTIONS`
 * 다. 그쪽이 바뀌면 여기가 조용히 낡으므로
 * `__tests__/v2-components/pdp-software-learn-more.test.ts` 가 두 목록을 대조한다.
 */
const DATA = [
  {
    image: `${BASE_PATH}/software_01_desktop@2x.jpg`,
    imageMobile: `${BASE_PATH}/software_01_mobile@3x.jpg`,
    title: "PC Player for PC gamers",
    detailLink: "/games/pc/software/",
    description:
      "Connect your device and play games instantly with one click, or enjoy videos and music through Audio-to-Haptics.",
  },
  {
    image: `${BASE_PATH}/software_02_desktop@2x.jpg`,
    imageMobile: `${BASE_PATH}/software_02_mobile@3x.jpg`,
    title: "VR Player for Meta Quest gamers",
    detailLink: "/games/vr/software/#sw-vr-player",
    description:
      "Enjoy seamless gameplay and total freedom in your VR experience.",
  },
  {
    image: `${BASE_PATH}/software_03_desktop@2x.jpg`,
    imageMobile: `${BASE_PATH}/software_03_mobile@3x.jpg`,
    title: "Mobile Player for PS VR 2 gamers",
    detailLink: "/games/vr/software/#sw-mobile-player",
    description:
      "Control your device anywhere and connect to PSVR2 for instant fun.",
  },
];

/**
 * 카드 폭이 뷰포트마다 달라 `sizes` 를 공식으로 잡는다. 카드 수는 항상 3.
 * - 모바일: 캐러셀이라 **280 고정**(28753:31196)
 * - 태블릿~: 컨테이너 min(1194, 100vw−32) 를 3등분, gap 25 × 2 = 50
 *   (1226 = 1194 + 32 이 상한이 걸리기 시작하는 지점)
 *
 * ⚠️ 피그마 `Lists` 는 **1196**(382×3 + 25×2)인데 `max-w-screen-mdl` 은 **1194**
 * (tailwind.config.ts:21)이라 실제 카드는 381 이다. 2px 차이는 **공용 토큰 값**이라
 * 이 섹션만 고치지 않는다(#5156 의 `coolgray-10` #F2F4F8 vs 피그마 #F2F4F7 과 같은 판단).
 * 여기 적는 값은 **코드 기준(1194)** 으로 맞춘다 — 피그마 숫자를 적어두면 다음 사람이
 * "컨테이너를 1196 으로 고쳐야 한다"로 읽는다.
 */
const CARD_SIZES = [
  `(min-width: 1226px) ${Math.floor((1194 - 50) / 3)}px`,
  "(min-width: 768px) calc((100vw - 82px) / 3)",
  "280px",
].join(", ");

export default function BhapticsCompanionSoftware({
  className,
}: {
  className?: string;
}) {
  const { t } = useTrans(["tactsuit"]);

  // 두 트리(태블릿 이상 grid · 모바일 캐러셀)가 공유한다. **DOM 에는 두 벌 다
  // 마운트된다**(CSS 토글) — 이미지 이중 요청은 없다(`src`·`sizes` 가 같아 같은
  // srcset 후보로 풀리고, `display:none` 인 lazy 이미지는 애초에 로드되지 않는다).
  const cards = (
    <>
      {DATA.map((item) => (
        <ImageCard
          key={item.title}
          imageSrc={item.image}
          title={t(item.title)}
          description={t(item.description)}
          detailLink={item.detailLink}
        />
      ))}
    </>
  );

  return (
    <div className={cn("mx-auto w-full", className)}>
      {/* 컨테이너 1196 (피그마 28715:139251 Lists w1196 = 382×3 + 25×2) */}
      <div className="mx-auto w-full max-w-screen-mdl">
        {/* 헤더는 **모든 구간에서 가운데**다 — 데스크톱(28715:139254 `text-center`)과
            모바일(28753:31190 `items-center` + `text-center`)이 같다.
            ⚠️ 같은 페이지의 `bHaptics Purchase Benefits` 는 모바일에서 좌측이라
            (28753:30721 `items-start`) 두 섹션 규칙이 다르다. 맞추지 말 것.
            제목→서브 20, 헤더→목록 24(모바일) / 40(데스크톱). */}
        <Title>{t("Bhaptics Companion Software")}</Title>
        <div className="mt-5">
          <SubHeading>
            {t(
              "Set up your device, play your favorite games and expand your immersion with our bHaptics Players.",
            )}
          </SubHeading>
        </div>

        {/* ── 태블릿 이상 ──
            ⚠️ `items-start` 다(피그마 `Lists`). grid 는 `align-items` 기본이
            `normal`(= stretch) 이라 빼면 카드가 등높이가 되고 CTA 가 정렬돼 버린다.
            본문 줄 수가 달라 CTA 가 계단식으로 어긋나는 게 의도다.
            태블릿도 3열로 둔다 — 1023(313) → 1024(314) 로 폭이 그대로 이어진다. */}
        <div className="mt-6 hidden items-start gap-4 tablet:grid tablet:grid-cols-3 tablet:gap-[25px] laptop:mt-10">
          {cards}
        </div>

        {/* ── 모바일: 가로 캐러셀 (피그마 28753:31194) ──
            카드 280 고정 · 간격 16 · 커버 280×280.
            ⚠️ **우측은 화면 끝까지 흘려보낸다**(`-mx-4` 로 상위 `Gap` 의 `px-4` 상쇄).
            `/games` 의 `DisplayNewGames` 와 같은 방식 — 다음 카드가 화면 가장자리에서
            잘려야 "더 있다" 가 읽힌다. ⚠️ 좌측 16 은 **슬라이드마다** 갖는다(`pl-4` 가 카드에). 트랙에 주면
            스크롤하는 순간 그 여백이 왼쪽으로 밀려나가 카드가 화면 끝에 붙는다
            (실측: 한 칸 스크롤 시 좌측 0). `DisplayNewGames` 가 슬라이드별 `pl-4` 를
            쓰는 이유다. 트랙의 `mr-4` 는 스크롤 끝에서 마지막 카드 우측 16. */}
        <DisplayGameCardsCarousel
          className="-mx-4 mt-6 tablet:hidden"
          enableNavigations={false}
        >
          <div className="mr-4 flex items-start">{cards}</div>
        </DisplayGameCardsCarousel>
      </div>
    </div>
  );
}

// 32/40(데스크톱) · 24/32(모바일), letterSpacing 0.
// ⚠️ `tracking-tight` 를 붙이지 말 것 — 피그마 Headline 은 letterSpacing 0 이고,
// 같은 페이지 다른 섹션 제목과 자간이 갈린다(#5163 에서 정리한 건).
const Title = ({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) => (
  <h2
    className={cn(
      "whitespace-pre-wrap text-center text-3xl font-400 text-coolgray-90 laptop:text-5xl",
      className,
    )}
  >
    {children}
  </h2>
);

// 14/18 ls.25(모바일) · 16/22 ls.5(데스크톱)
const SubHeading = ({
  children,
  className,
}: React.PropsWithChildren<{ className?: string }>) => (
  <p
    className={cn(
      "whitespace-pre-wrap text-center text-base font-400 tracking-[0.25px] text-coolgray-90 laptop:text-lg laptop:tracking-[0.5px]",
      className,
    )}
  >
    {children}
  </p>
);

const ImageCard = ({
  imageSrc,
  title,
  description,
  detailLink,
  className,
}: React.PropsWithChildren<{
  className?: string;
  imageSrc: string;
  title: string;
  description: string;
  detailLink: string;
}>) => {
  const { t } = useTrans(["gnb"]);
  return (
    // 모바일은 캐러셀 슬라이드라 280 고정, 태블릿부터는 트랙이 폭을 정한다.
    // 커버↔본문 20 (피그마 Item `gap-[20px]`).
    <div
      className={cn(
        "group flex w-[296px] shrink-0 flex-col gap-5 pl-4 tablet:w-auto tablet:shrink tablet:pl-0",
        className,
      )}
    >
      {/* 커버 종횡비가 **뷰포트마다 다르다** — 모바일 280×280 정사각(28753:31196),
          데스크톱 382×407 세로형(28715:139259). 피그마 원본 그대로다(통일하지 말 것).
          ⚠️ 전환점을 `tablet`(768)에 둔다 — **레이아웃이 갈리는 지점과 같다**(캐러셀→grid).
          `laptop`(1024)에 두면 768–1023 만 정사각이 돼, 폭이 그대로인데(314) 높이만
          21px 튀는 구간이 생긴다(1023 314×314 → 1024 314×335). 피그마에 태블릿 스펙이
          없어 우리가 정하는 값이고, §2 에서 태블릿 열 수를 laptop 과 잇도록 고른 것과
          같은 기준이다 — 다른 게 안 바뀌는 지점에서 혼자 튀지 않게 한다. */}
      <Link
        href={detailLink}
        className="relative block aspect-square overflow-hidden rounded-2xl tablet:aspect-[382/407]"
      >
        <Image
          src={imageSrc}
          fill
          sizes={CARD_SIZES}
          alt={title}
          className="hover-scale object-cover"
        />
      </Link>
      {/* 본문 내부 12, 본문↔링크 8 (피그마 `gap-[12px]` / `gap-[8px]`) */}
      <div className="flex flex-col gap-2">
        <div className="flex flex-col gap-3">
          <h3 className="text-2xl text-coolgray-90">{title}</h3>
          <p className="text-base tracking-[0.25px] text-coolgray-90">
            {description}
          </p>
        </div>
        <Link href={detailLink} className={PDP_TEXT_LINK}>
          {t("Learn More")}
        </Link>
      </div>
    </div>
  );
};
